import type { DesignSystemState } from '@/features/design-system/lib/design-system-options'
import type { PromptBlockSpecV3 } from '@/services/block-api-service'

export type ConversationPhase =
  | 'idle'        // No active generation
  | 'gathering'   // Collecting narrative + goals from user
  | 'scenarios'   // Generating interaction scenarios
  | 'pages'       // Generating page definitions
  | 'page-ui'     // Generating page UI (Tailwind HTML → Coogo JSON)
  | 'theming'     // Selecting theme/design
  | 'generating'  // App generation in progress
  | 'complete'    // Pipeline finished

export interface ConfirmedScenario {
  name: string
  description: string
}

export interface ConfirmedPage {
  id: string
  title: string
  route: string
  blocks: string[]
}

export interface ConfirmedBlock {
  name: string
  type: string
  description: string
  binds: string[]
  specV3?: PromptBlockSpecV3
}

export interface ThemeHint {
  primaryColor: string
  style: string
  fontFamily?: string
}

export interface ConversationState {
  phase: ConversationPhase
  appName: string | null
  tier: 'A' | 'B' | null
  confirmedNarrative: string | null
  confirmedGoals: string[]
  confirmedScenarios: ConfirmedScenario[]
  confirmedPages: ConfirmedPage[]
  confirmedBlocks: ConfirmedBlock[]
  themeHint: ThemeHint | null
  /** Selected design system preset tokens for generation */
  designSystemPreset: DesignSystemState | null
}

export const INITIAL_CONVERSATION_STATE: ConversationState = {
  phase: 'idle',
  appName: null,
  tier: null,
  confirmedNarrative: null,
  confirmedGoals: [],
  confirmedScenarios: [],
  confirmedPages: [],
  confirmedBlocks: [],
  themeHint: null,
  designSystemPreset: null,
}

// ─── Stepper Phase Mapping ─────────────────────────────────────

export type StepperStep = 'describe' | 'scenarios' | 'pages' | 'generate' | 'complete'

export const STEPPER_STEP_ORDER: StepperStep[] = ['describe', 'scenarios', 'pages', 'generate', 'complete']

export function phaseToStepperStep(phase: ConversationPhase): StepperStep | null {
  switch (phase) {
    case 'idle': return null
    case 'gathering': return 'describe'
    case 'scenarios': return 'scenarios'
    case 'pages':
    case 'page-ui':
    case 'theming': return 'pages'
    case 'generating': return 'generate'
    case 'complete': return 'complete'
    default: return null
  }
}
